import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";

import { formatEuro } from "../../../../../../../format/formatNumber";
import { IOptionItem } from "../../../../../../../formik/formik-autocomplete/FormikAutocomplete.types";
import { FinancialHolding } from "../../../../../../api/ClientFileApi";
import { useAssetInventoryApi } from "../../../../../../api/ClientFileApiContext";
import { useClientFileContext } from "../../../../../../context/ClientFileContext";
import { ClientFileQueryKey } from "../../../../../../query/clientFileQueryKeys";
import { ClaimCollateralFormValues, NEW_HOLDING_OPTION_ID } from "../../../ClaimFormFields.types";
import { HOLDING_COLLATERAL_TYPES, HoldingCollateralType } from "../Collaterals.utils";

const LABEL_SEPARATOR = " · ";

/**
 * The assets of the client's asset schedule a security can refer to. Read with the asset schedule's own query,
 * so an asset edited there shows its current data here too.
 */
export function useCollateralHoldingOptions(collaterals: ClaimCollateralFormValues[]) {
  const { t, i18n } = useTranslation();
  const { clientId } = useClientFileContext();
  const assetInventoryApi = useAssetInventoryApi();

  const { data } = useQuery({
    queryKey: [ClientFileQueryKey.AssetInventoryAssets, clientId],
    queryFn: () => assetInventoryApi.getAssetInventoryAssets({ clientId }),
    enabled: !!clientId,
  });

  const holdings = useMemo(() => data?.financialHoldings ?? [], [data]);

  const holdingLabel = useCallback(
    (holding: FinancialHolding) =>
      [
        holding.institutionName ?? t("claims.collaterals.holdingInstitutionMissing"),
        holding.accountNumber ?? t("claims.collaterals.holdingNumberMissing"),
        ...(holding.value === null ? [] : [formatEuro(holding.value, i18n.language)]),
      ].join(LABEL_SEPARATOR),
    [t, i18n.language]
  );

  /**
   * The options of one security's asset dropdown: the assets of the type's kind that no other security of the
   * claim refers to, then the option that creates a new asset.
   */
  const optionsFor = useCallback(
    (type: HoldingCollateralType, collateralKey: string): IOptionItem[] => {
      const takenByOthers = new Set(
        collaterals
          .filter((collateral) => collateral.key !== collateralKey)
          .map((collateral) => collateral.holdingSelection)
          .filter((selection): selection is string => !!selection && selection !== NEW_HOLDING_OPTION_ID)
      );

      return [
        ...holdings
          .filter((holding) => holding.kind === HOLDING_COLLATERAL_TYPES[type].kind && !takenByOthers.has(holding.id))
          .map((holding) => ({ id: holding.id, label: holdingLabel(holding) })),
        { id: NEW_HOLDING_OPTION_ID, label: t(`claims.collaterals.newHoldingOption.${type}`) },
      ];
    },
    [collaterals, holdings, holdingLabel, t]
  );

  return { optionsFor };
}
